import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Scroll restoration that ALSO targets the inner `<main data-scroll-root>`
 * element used by Layout.jsx (the app scrolls inside that container, not on
 * the window). Behaviour:
 *  - PUSH: scroll both window and the scroll-root to (0, 0).
 *  - POP / REPLACE: restore the previously saved scrollTop on the scroll-root,
 *    with a retry loop because async-loaded pages grow scrollHeight over time
 *    (a single rAF would be clamped to 0 before content renders).
 */
function getScrollRoot() {
  return document.querySelector("[data-scroll-root]") || null;
}

const RESTORE_TIMINGS_MS = [16, 50, 200, 600, 1200]; // best-effort retry budget

export default function ScrollRestorer() {
  const { pathname } = useLocation();
  const navType = useNavigationType();

  // restore / scroll-to-top on path change
  useEffect(() => {
    let cancelled = false;
    const saved = sessionStorage.getItem(`scroll::${pathname}`);
    const root = getScrollRoot();

    if (navType === "PUSH") {
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        if (root) root.scrollTop = 0;
      });
      return () => { cancelled = true; };
    }

    // POP / REPLACE → restore saved Y. Retry across paint frames until the
    // scrollHeight catches up to the saved offset (async-loaded content).
    const y = saved != null ? (parseInt(saved, 10) || 0) : 0;
    if (y <= 0) return () => { cancelled = true; };

    const applyOnce = () => {
      if (cancelled) return false;
      const r = getScrollRoot();
      if (r) {
        // Browsers clamp scrollTop to max scrollable. So once scrollHeight is
        // tall enough, scrollTop will actually hold the requested value.
        r.scrollTop = y;
        if (r.scrollTop === y) return true; // success
      }
      window.scrollTo(0, y);
      return window.scrollY === y;
    };

    const timeouts = RESTORE_TIMINGS_MS.map((ms) =>
      setTimeout(() => { applyOnce(); }, ms),
    );

    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
    };
  }, [pathname, navType]);

  // save scroll position on every scroll (both window and the scroll-root container)
  useEffect(() => {
    const save = () => {
      const root = getScrollRoot();
      const y = root ? root.scrollTop : window.scrollY;
      sessionStorage.setItem(`scroll::${pathname}`, String(y));
    };
    const root = getScrollRoot();
    window.addEventListener("scroll", save, { passive: true });
    if (root) root.addEventListener("scroll", save, { passive: true });
    return () => {
      window.removeEventListener("scroll", save);
      if (root) root.removeEventListener("scroll", save);
    };
  }, [pathname]);

  return null;
}
